'use client';

import { useCallback } from 'react';
import {
	encodeName,
	getReferrerNamePublicKeySync,
	getUserStatsAccountPublicKey,
} from '@drift-labs/sdk';
import { SYSVAR_RENT_PUBKEY, SystemProgram } from '@solana/web3.js';
import invariant from 'tiny-invariant';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useDriftClient from 'src/hooks/useDriftClient';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';

/**
 * Hook for creating referral links.
 */
export const useCreateReferralLink = () => {
	const driftClient = useDriftClient();
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const { captureEvent } = usePostHogCapture();
	const getTxParams = useGetTxParams();
	const currentUserAccount = useCurrentUserAccount();

	const currentUserAccountAuthority = currentUserAccount?.authority;
	const currentUserAccountPublicKey = currentUserAccount?.pubKey;

	// TODO: abstract into @drift/common
	const createReferralLinkAction = useCallback(
		async (wallet: UiEnhancedIWalletV2, referrerName: string) => {
			invariant(driftClient, 'driftClient is required');
			invariant(
				currentUserAccountAuthority,
				'currentUserAccountAuthority is required'
			);
			invariant(
				currentUserAccountPublicKey,
				'currentUserAccountPublicKey is required'
			);

			const userStatsAccountPublicKey = getUserStatsAccountPublicKey(
				driftClient.program.programId,
				currentUserAccountAuthority
			);

			const nameBuffer = encodeName(referrerName);
			const referrerNameAccountPublicKey = getReferrerNamePublicKeySync(
				driftClient.program.programId,
				nameBuffer
			);
			const ix = await driftClient.program.instruction.initializeReferrerName(
				nameBuffer,
				{
					accounts: {
						referrerName: referrerNameAccountPublicKey,
						user: currentUserAccountPublicKey,
						authority: currentUserAccountAuthority,
						userStats: userStatsAccountPublicKey,
						payer: driftClient.wallet.publicKey,
						rent: SYSVAR_RENT_PUBKEY,
						systemProgram: SystemProgram.programId,
					},
				}
			);
			await wallet.signAndSendTransaction(ix, getTxParams());
		},
		[
			driftClient,
			currentUserAccountAuthority,
			currentUserAccountPublicKey,
			getTxParams,
		]
	);

	const wrappedCreateReferralLink = useSponsoredSignDriftActionWithFallback(
		createReferralLinkAction
	);

	const createReferralLink = useCallback(
		async (referrerName: string): Promise<boolean> => {
			if (isGeoblocked) {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Drift from a restricted territory',
					lengthMs: 5000,
				});
				return false;
			}

			if (!driftClient) return false;

			const toastId = `create-referral-link-${referrerName}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedCreateReferralLink(referrerName);

				notify({
					type: 'success',
					message: 'Referral link created.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return true;
			} catch (e: any) {
				captureEvent('referral_creation_error', {
					error: e,
				});
				console.error(e);
				notify({
					type: 'error',
					message: 'Could not create referral link.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				return false;
			}
		},
		[driftClient, isGeoblocked, captureEvent, wrappedCreateReferralLink]
	);

	return { createReferralLink };
};
